// KI-gestützte Dokumentenextraktion via Claude Vision API
// Unterstützte Formate: JPG, PNG, WEBP, GIF, PDF (auch gescannt/handschriftlich)
// DSGVO: Nur administrative Daten senden (keine medizinischen Patientendaten)

const LS_KEY = 'amed_anthropic_api_key';

export interface ExtractedDocData {
  doc_type:               'mandant' | 'arbeitnehmer' | 'unbekannt';
  firma?:                  string;
  kurz_code?:              string;
  strasse?:                string;
  plz?:                    string;
  ort?:                    string;
  land?:                   string;
  telefon?:                string;
  email?:                  string;
  branche?:                string;
  mitarbeiterzahl?:        number | null;
  ansprechperson?:         string;
  ansprechperson_titel?:   string;
  ansprechperson_email?:   string;
  ansprechperson_telefon?: string;
  person_vorname?:         string;
  person_nachname?:        string;
  person_geburtsdatum?:    string;
  notizen?:                string;
}

export function getAnthropicKey(): string | null {
  return localStorage.getItem(LS_KEY);
}

export function setAnthropicKey(key: string): void {
  const trimmed = key.trim();
  if (trimmed) {
    localStorage.setItem(LS_KEY, trimmed);
  } else {
    localStorage.removeItem(LS_KEY);
  }
}

export function isAnthropicConfigured(): boolean {
  return Boolean(localStorage.getItem(LS_KEY));
}

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const b64 = result.split(',')[1];
      if (b64 === undefined) { reject(new Error('Base64-Konvertierung fehlgeschlagen')); return; }
      resolve(b64);
    };
    reader.onerror = () => { reject(new Error('Datei konnte nicht gelesen werden')); };
    reader.readAsDataURL(file);
  });
}

const EXTRACT_PROMPT = `Analysiere dieses Dokument und extrahiere strukturierte Daten.
Gib das Ergebnis AUSSCHLIESSLICH als JSON zurück, kein erklärender Text, keine Markdown-Formatierung.

JSON-Schema (alle Felder optional, null wenn nicht vorhanden):
{
  "doc_type": "mandant" | "arbeitnehmer" | "unbekannt",
  "firma": string | null,
  "kurz_code": string | null,
  "strasse": string | null,
  "plz": string | null,
  "ort": string | null,
  "land": string | null,
  "telefon": string | null,
  "email": string | null,
  "branche": string | null,
  "mitarbeiterzahl": number | null,
  "ansprechperson": string | null,
  "ansprechperson_titel": string | null,
  "ansprechperson_email": string | null,
  "ansprechperson_telefon": string | null,
  "person_vorname": string | null,
  "person_nachname": string | null,
  "person_geburtsdatum": string | null,
  "notizen": string | null
}

Kontext: Österreichische Arbeitsmedizin-Praxis.
- Firmenkontaktblätter / Visitenkarten / Briefköpfe → doc_type: "mandant"
- Dienstzettel / Personalbögen / Anmeldeformulare → doc_type: "arbeitnehmer"
- kurz_code: 2–4 Buchstaben aus dem Firmennamen ableiten (z.B. "Ramsauer & Stürmer" → "RAM")
- person_geburtsdatum: Format DD.MM.YYYY
- Handgeschriebenes sorgfältig lesen und entziffern

Gib NUR den JSON-Block zurück.`;

export async function extractDocumentData(file: File): Promise<ExtractedDocData> {
  const apiKey = getAnthropicKey();
  if (!apiKey) {
    throw new Error('Kein Anthropic API Key konfiguriert. Bitte in Einstellungen → KI-Assistent hinterlegen.');
  }

  const isPdf    = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  const isImage  = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].some(t => file.type === t);

  if (!isPdf && !isImage) {
    throw new Error('Dateityp nicht unterstützt. Bitte JPG, PNG, WEBP oder PDF verwenden.');
  }

  const base64    = await readFileAsBase64(file);
  const mediaType = isPdf ? 'application/pdf' : file.type;
  return extractFromBase64(base64, mediaType, isPdf);
}

/** Wie extractDocumentData, aber direkt aus Base64 (z.B. aus der Handy-Erfassung). */
export async function extractDocumentDataFromBase64(
  base64: string,
  mediaType: string,
): Promise<ExtractedDocData> {
  return extractFromBase64(base64, mediaType, mediaType === 'application/pdf');
}

async function extractFromBase64(
  base64: string,
  mediaType: string,
  isPdf: boolean,
): Promise<ExtractedDocData> {
  const apiKey = getAnthropicKey();
  if (!apiKey) {
    throw new Error('Kein Anthropic API Key konfiguriert. Bitte in Einstellungen → Sicherheit hinterlegen.');
  }

  type ContentBlock =
    | { type: 'image';    source: { type: 'base64'; media_type: string; data: string } }
    | { type: 'document'; source: { type: 'base64'; media_type: string; data: string } }
    | { type: 'text';     text: string };

  const docBlock: ContentBlock = isPdf
    ? { type: 'document', source: { type: 'base64', media_type: mediaType, data: base64 } }
    : { type: 'image',    source: { type: 'base64', media_type: mediaType, data: base64 } };

  const content: ContentBlock[] = [docBlock, { type: 'text', text: EXTRACT_PROMPT }];

  const body = {
    model:      'claude-haiku-4-5-20251001',
    max_tokens: 1024,
    messages:   [{ role: 'user', content }],
  };

  let response: Response;
  try {
    response = await fetch('https://api.anthropic.com/v1/messages', {
      method:  'POST',
      headers: {
        'x-api-key':                             apiKey,
        'anthropic-version':                     '2023-06-01',
        'content-type':                          'application/json',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error(`Netzwerkfehler: ${e instanceof Error ? e.message : String(e)}`);
  }

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    if (response.status === 401) throw new Error('Ungültiger Anthropic API Key. Bitte in Einstellungen prüfen.');
    if (response.status === 429) throw new Error('API-Limit erreicht. Kurz warten und erneut versuchen.');
    throw new Error(`API-Fehler ${response.status}: ${errText.slice(0, 200)}`);
  }

  const result = await response.json() as { content: Array<{ type: string; text?: string }> };
  const rawText = result.content.find(c => c.type === 'text')?.text ?? '';

  // Strip optional markdown fences
  const clean = rawText
    .replace(/^```(?:json)?\s*/m, '')
    .replace(/\s*```\s*$/m, '')
    .trim();

  try {
    return JSON.parse(clean) as ExtractedDocData;
  } catch {
    throw new Error('KI-Antwort konnte nicht verarbeitet werden. Bitte erneut versuchen.');
  }
}
